// frontend/src/modules/reports/pages/workforce/HoursSummaryPage.tsx
//
// Phase 4 (Reports Frontend), Task 1. Reference report page — the pattern
// Tasks 2/3/4 copy: ReportShell owns the date range + CSV chrome, this page
// owns fetching (useApi keyed on the resolved range + its own filters),
// stat tiles, and a basis-aware table.
//
// `basis` honesty rule (Phase-3 contract §8): when the tenant has scheduling
// OFF, expectations come from company-hours/employee-default fallback, not a
// real shift schedule — the "Scheduled" column/stat must relabel to
// "Company hours" in that case so the report never implies data that
// doesn't exist.

import * as React from 'react';
import { Clock3, Gauge, TimerReset } from 'lucide-react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { DataTable } from '../../../../design-system/components/DataTable';
import { useApi } from '../../../../design-system/useApi';
import { reportUrl, downloadReportCsv } from '../../api';
import type { HoursSummaryReport, GroupBy } from '../../types';
import { VISUAL } from '../../../../design-system/visual';

const GROUP_BYS: GroupBy[] = ['day', 'week', 'month'];

/** '125' minutes -> '2h 5m'. */
function fmtHours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function fmtPct(n: number | null): string {
  return n === null ? '—' : `${Math.round(n * 100)}%`;
}

export function HoursSummaryPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [groupBy, setGroupBy] = React.useState<GroupBy>('day');

  const path = range ? reportUrl('hours-summary', { from: range.from, to: range.to, groupBy }) : null;
  const result = useApi<{ report: HoursSummaryReport }>(path, [groupBy]);
  const forbidden = result.status === 403;

  const report = result.data?.report ?? null;
  const rows = report?.rows ?? [];
  const basis = report?.basis;
  // Honest-labeling rule: never say "Scheduled" when there's no real schedule.
  const scheduledLabel = basis === 'company_hours' ? 'Company hours' : 'Scheduled';

  const totals = React.useMemo(() => {
    const scheduled = rows.reduce((s, r) => s + r.scheduled_minutes, 0);
    const active = rows.reduce((s, r) => s + r.active_minutes, 0);
    const paused = rows.reduce((s, r) => s + r.paused_minutes, 0);
    const utilization = scheduled > 0 ? active / scheduled : null;
    return { scheduled, active, paused, utilization };
  }, [rows]);

  return (
    <ReportShell
      eyebrow="Reports / Workforce"
      title="Hours summary"
      description="Scheduled vs. worked and active hours per employee, bucketed by day, week, or month."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      filters={
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
          {GROUP_BYS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              className={[
                'px-3.5 h-8 rounded-full text-[12px] font-semibold capitalize transition-colors',
                groupBy === g ? 'bg-saffron text-zinc-950' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {g}
            </button>
          ))}
        </div>
      }
      csv={range ? {
        filename: `hours-summary-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'hours-summary',
          { from: range.from, to: range.to, groupBy },
          `hours-summary-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label={scheduledLabel} value={fmtHours(totals.scheduled)} icon={<Clock3 />} />
            <StatCard label="Active" value={fmtHours(totals.active)} icon={<TimerReset />} />
            <StatCard label="Utilization" value={fmtPct(totals.utilization)} icon={<Gauge />} />
          </div>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable
              rows={rows}
              rowKey={(r) => `${r.employee_id}-${r.bucket}`}
              columns={[
                { key: 'employee', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
                { key: 'bucket', header: 'Period', cell: (r) => r.bucket },
                { key: 'scheduled', header: scheduledLabel, cell: (r) => fmtHours(r.scheduled_minutes) },
                { key: 'worked', header: 'Worked', cell: (r) => fmtHours(r.worked_minutes) },
                { key: 'active', header: 'Active', cell: (r) => fmtHours(r.active_minutes) },
                { key: 'paused', header: 'Paused', cell: (r) => fmtHours(r.paused_minutes) },
                { key: 'utilization', header: 'Utilization', cell: (r) => fmtPct(r.utilization) },
              ]}
            />
          </div>
        </>
      )}
    </ReportShell>
  );
}
